import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BatchArchiveOpportunityDto,
  CreateSharedStatusDto,
  OneWayInOpportunityCreateDto,
  OneWayInOpportunityStatusCreateDto,
  OneWayInRecommendation,
  OneWayInService,
} from 'src/api/v2';

import { useTranslation } from 'react-i18next';
import {
  ONE_WAY_IN_ALL_FORMS_KEY,
  ONE_WAY_IN_ALL_SAVED_FORMS_KEY,
  ONE_WAY_IN_FORM_FULL_HISTORY,
  ONE_WAY_IN_FORM_IN_RESPONSE_MAIL,
  ONE_WAY_IN_OPPORTUNITY_BY_ID,
  ONE_WAY_IN_GROUPED_FORM,
  ONE_WAY_IN_GROUPED_FORM_ALL,
  ONE_WAY_IN_FEEDBACK,
  ONE_WAY_IN_GROUPED_FORM_ENROLLMENT,
} from 'src/constants/hook-keys';
import { useAuth } from 'src/features/authorization';
import { HOTJAR_DECLARATIONS } from 'src/features/tracking/constants';
import useHotjar from 'src/hooks/use-hotjar';

export interface OneWayInOpportunityAddMemberDto {
  organizationNumber: string;
  oneWayInId: string;
}

const useOneWayIn = (formId: string | undefined = '') => {
  const [oneWayInService] = useState(new OneWayInService());
  const { t: tCommon } = useTranslation('common');
  const hotjar = useHotjar();
  const { checkAccess } = useAuth();
  const { read, write } = checkAccess('OneWayIn');

  const queryClient = useQueryClient();

  const oneWayInOpportunityById = useQuery({
    queryKey: [ONE_WAY_IN_OPPORTUNITY_BY_ID, formId],
    enabled: !!formId && read,
    queryFn: () => oneWayInService.getOpportunity(formId),
    staleTime: 1000 * 60 * 15, // 15 min refetching on mount
    refetchOnMount: true,
    retry: false,
  });

  const oneWayInGroupedFormQuery = useQuery({
    queryKey: [ONE_WAY_IN_GROUPED_FORM_ALL, formId],
    queryFn: () => oneWayInService.getOpportunityForm(formId),
    enabled: !!formId && read,
    staleTime: 1000 * 60 * 15, // 15 min refetching on mount
    refetchOnMount: true,
  });

  const oneWayInGroupedFormWOEnrollmentsQuery = useQuery({
    queryKey: [ONE_WAY_IN_GROUPED_FORM, formId],
    queryFn: () => oneWayInService.getGroupedFormDetailsBare(formId, false),
    enabled: !!formId && read,
    retry: false,
    staleTime: 1000 * 60 * 15, // 15 min refetching on mount
    refetchOnMount: true,
  });

  const oneWayInGroupedFormEnrollment = useQuery({
    queryKey: [ONE_WAY_IN_GROUPED_FORM_ENROLLMENT, formId],
    queryFn: () => oneWayInService.getFormEnrollments(formId),
    enabled: !!formId && read,
    retry: false,
    staleTime: 1000 * 60 * 15, // 15 min refetching on mount
    refetchOnMount: true,
  });

  const oneWayInOpportunityFormFullHistoryQuery = useQuery({
    queryKey: [ONE_WAY_IN_FORM_FULL_HISTORY, formId],
    queryFn: () => oneWayInService.getOneWayInOpportunityFullHistory(formId),

    enabled: !!formId && !!oneWayInOpportunityById.data && read,
    retry: false,
    staleTime: 1000 * 60 * 15, // 15 min refetching on mount
    refetchOnMount: true,
    keepPreviousData: false,
  });

  const onewWayInOpportunityFormINResponseMailQuery = useQuery({
    queryKey: [ONE_WAY_IN_FORM_IN_RESPONSE_MAIL, formId],
    queryFn: () => {
      return oneWayInService.getInquiryINResponse(formId);
    },

    enabled: !!formId && !!oneWayInOpportunityById.data && read,
    retry: false,
    refetchOnMount: false,
    keepPreviousData: false,
  });

  const updateOneWayInOpportunityStatus = useMutation({
    mutationFn: (updateDto: OneWayInOpportunityStatusCreateDto) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }
      if (updateDto.description !== undefined) {
        hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.NOTE_ADDED}`);
      } else if (updateDto.registeredServices.length > 0) {
        hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.SERVICE_DELIVERED}`);
      } else if (updateDto.recommendedServices.length > 0) {
        hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.SERVICE_RECOMMENDED}`);
      }
      return oneWayInService.putWayInOpportunityStatus(updateDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_FEEDBACK, formId]);
      return queryClient.invalidateQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
    },
  });

  const batchArchiveOneWayInOpportunity = useMutation({
    mutationFn: (batchArchiveOpportunityDto: BatchArchiveOpportunityDto) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }

      return oneWayInService.archiveOneWayInOpportunity(batchArchiveOpportunityDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
      return queryClient.invalidateQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
    },
  });

  const toggleCloseOneWayInOpportunity = useMutation({
    mutationFn: (updateDto: OneWayInOpportunityStatusCreateDto) => {
      if (!write) {
        throw new Error(tCommon('cannotWrite'));
      }
      hotjar.log(`${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.OPPORTUNITY_ARCHIVED}`);
      return oneWayInService.putWayInOpportunityStatus(updateDto);
    },
    onSuccess: () => {
      queryClient.resetQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      queryClient.resetQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
    },
  });

  const shareOneWayInOpportunity = useMutation({
    mutationFn: ({
      createDto,
      opportunityId,
    }: {
      createDto: CreateSharedStatusDto;
      opportunityId: string;
    }) => {
      if (!write || !formId) {
        throw new Error(tCommon('cannotWrite'));
      }

      return oneWayInService.shareOpportunity(opportunityId, createDto);
    },
    onSuccess: () => {
      queryClient.resetQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      oneWayInOpportunityById.refetch();
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
    },
  });

  const recommendService = useMutation({
    mutationFn: (recommendation: OneWayInRecommendation) => {
      if (!write || !formId) {
        throw new Error(tCommon('cannotWrite'));
      }
      return oneWayInService.recommendServices(recommendation);
    },
    onSuccess: () => {
      queryClient.resetQueries([ONE_WAY_IN_FORM_FULL_HISTORY, formId]);
      oneWayInOpportunityById.refetch();
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_FEEDBACK, formId]);
    },
    retry: false,
  });

  const addMember = useMutation({
    mutationFn: (addMemberDto: OneWayInOpportunityAddMemberDto) =>
      oneWayInService.addMember(addMemberDto.organizationNumber, addMemberDto.oneWayInId),

    onSuccess: () => {
      queryClient.invalidateQueries([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
    },
    retry: false,
  });

  const createOpportunity = useMutation({
    mutationFn: (createDto: OneWayInOpportunityCreateDto) =>
      oneWayInService
        .createOneWayInOpportunity(createDto)
        .then((data) => queryClient.setQueryData([ONE_WAY_IN_OPPORTUNITY_BY_ID, formId], data)),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_FORMS_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
    },
  });

  return {
    oneWayInGroupedFormWOEnrollmentsQuery,
    oneWayInGroupedFormQuery,
    oneWayInGroupedFormEnrollment,
    updateOneWayInOpportunityStatus,
    oneWayInOpportunityFormFullHistoryQuery,
    onewWayInOpportunityFormINResponseMailQuery,
    shareOneWayInOpportunity,
    recommendService,
    toggleCloseOneWayInOpportunity,
    batchArchiveOneWayInOpportunity,
    addMember,
    oneWayInOpportunityById,
    createOpportunity,
  };
};

export default useOneWayIn;
